import * as React from 'react';
import { scrollToAnchor } from '@cian/newbuilding-utils';
import { useSelector } from 'react-redux';

import { Genplan } from '../../components/Genplan';
import { House } from '../../components/Genplan/components/House';
import { HouseTooltip } from '../../components/Genplan/components/HouseTooltip';
import { HouseTooltipAB } from '../../components/Genplan/components/HouseTooltipAB';
import { getGenplanSelector, getNewbuildingId } from '../../selectors/newbuilding';
import { isGenplanExperimentEnable } from '../../selectors/offers';
import {
  trackShowGenplan,
  trackHouseHover,
  trackHouseLinkClick,
  trackGenplanReliableLabelClick,
  trackGenplanEscrowLabelClick,
} from './tracking';

export function GenplanContainer() {
  const genplan = useSelector(getGenplanSelector);
  const isAbExperiment = useSelector(isGenplanExperimentEnable);
  const HouseTooltipComponent = isAbExperiment ? HouseTooltipAB : HouseTooltip;
  const newbuildingId = useSelector(getNewbuildingId);
  const [viewedHouses, setViewedHouses] = React.useState<number[]>([]);

  const onHover = React.useCallback(
    (houseId: number) => {
      /* istanbul ignore else */
      if (!viewedHouses.includes(houseId)) {
        trackHouseHover(newbuildingId, houseId);
        setViewedHouses([...viewedHouses, houseId]);
      }
    },
    [newbuildingId, viewedHouses],
  );

  const onHouseLinkClick = React.useCallback(
    (houseId: number, roomType: string) => {
      trackHouseLinkClick(newbuildingId, houseId, roomType);
    },
    [newbuildingId],
  );

  if (!genplan) {
    return null;
  }

  /* istanbul ignore next */
  const onReliableLabelClick = (houseId: number) => (event: React.MouseEvent<HTMLElement>) => {
    scrollToAnchor({
      anchor: '#reliable',
      offset: 0,
      hasRelocation: false,
      hasAnimation: true,
    })(event);
    trackGenplanReliableLabelClick(newbuildingId, houseId);
  };

  /* istanbul ignore next */
  const onEscrowLabelClick = (houseId: number) => (event: React.MouseEvent<HTMLElement>) => {
    scrollToAnchor({
      anchor: '#escrow',
      offset: 100,
      hasRelocation: false,
      hasAnimation: true,
    })(event);
    trackGenplanEscrowLabelClick(newbuildingId, houseId);
  };

  const { image, isHousesNamesEnabled, houses } = genplan;

  return (
    <Genplan
      image={image}
      onShow={trackShowGenplan}
      renderHouses={(containerWidth: number, containerHeight: number) =>
        houses.map(({ name, houseInfo, stats, isReliable, isEscrow, isSalesStart, ...house }) => (
          <House
            key={house.id}
            {...house}
            name={name}
            isReliable={isReliable}
            isEscrow={isEscrow}
            isSalesStart={isSalesStart}
            emptyStats={!stats}
            showTitle={isHousesNamesEnabled}
            containerWidth={containerWidth}
            containerHeight={containerHeight}
            tooltip={
              <HouseTooltipComponent
                name={name}
                houseId={house.id}
                houseInfo={houseInfo}
                stats={stats}
                isReliable={isReliable}
                isEscrow={isEscrow}
                isSalesStart={isSalesStart}
                onLinkClick={onHouseLinkClick}
                onReliableLabelClick={onReliableLabelClick(house.id)}
                onEscrowLabelClick={onEscrowLabelClick(house.id)}
              />
            }
            onHover={() => onHover(house.id)}
          />
        ))
      }
    />
  );
}
